import { decode, encode } from '@googlemaps/polyline-codec';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import {
  deriveCheckpointsFromTour,
  resolveTourStartLatLng,
  toCheckpoint,
  toLatLng,
} from 'features/Responder/features/Tours/utils';
import { buildTourGeometryFocusKey } from 'features/Responder/utils/focusKeys';
import { focusMapOnGeometry } from 'features/Responder/utils/focusMapOnGeometry';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Marker, Polygon, Polyline } from 'react-leaflet';
import './css/TourMap.css';
// CheckpointOverlay import retained for reference.
// import CheckpointOverlay from './CheckpointOverlay';
import BaseMap from 'components/BaseMap/BaseMap';
import { getClosestPointOnPolyline } from 'features/Responder/features/Tours/features/TourBuilder/utils/closestPointOnPolyline';
import { getDistanceToPolyline } from 'features/Responder/features/Tours/features/TourBuilder/utils/distanceToPolyline';
import { resolveZonePolygon } from 'features/Responder/features/Tours/features/TourBuilder/utils/zoneRouteMath';
import CheckpointsLayer from './Components/CheckpointsLayer';
import GoogleLocationSearchControl from './Components/GoogleLocationSearchControl';
import MapClickHandler from './Components/MapClickHandler';
import NodeRadiusLayer from './Components/NodeRadiusLayer';
import PersonnelLayerWithLiveData from './Components/PersonnelLayerWithLiveData';
import RouteLayer from './Components/RouteLayer';
import TourToolsPanel from './Components/TourToolsPanel';
import { useCheckpointActions } from './hooks/useCheckpointActions';
import { useFitBounds } from './hooks/useFitBounds';
import { useRouteProjection } from './hooks/useRouteProjection';
import { useUserLocation } from './hooks/useUserLocation';
import { almostEqual, parseCenterLatLng } from './utils/centerMath';
import round4 from './utils/round4';
import { getCumulativeEtaSeconds, getLegStartsMeters } from './utils/routeLegMetrics';

const hashString = (value = '') => {
  const str = `${value}`;
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16);
};

const DEFAULT_ORIGIN_RADIUS_METERS = 25;
const DEFAULT_DESTINATION_RADIUS_METERS = 100;
const DEFAULT_CHECKPOINT_RADIUS_METERS = 75;
const NODE_MATCH_TOLERANCE = 1e-5;

const normalizeNodeKind = (kind) => {
  const normalized = `${kind ?? ''}`.toLowerCase().trim();
  if (normalized === 'start') return 'origin';
  if (normalized === 'end') return 'destination';
  return normalized || 'checkpoint';
};

const sameLatLng = (a, b, tolerance = NODE_MATCH_TOLERANCE) => {
  const aLat = Number(a?.lat);
  const aLng = Number(a?.lng);
  const bLat = Number(b?.lat);
  const bLng = Number(b?.lng);
  if (
    !Number.isFinite(aLat) ||
    !Number.isFinite(aLng) ||
    !Number.isFinite(bLat) ||
    !Number.isFinite(bLng)
  ) {
    return false;
  }
  return Math.abs(aLat - bLat) <= tolerance && Math.abs(aLng - bLng) <= tolerance;
};

const isZoneLike = (node = {}) => {
  const geometry = node?.geometry || {};
  const type = `${geometry?.type ?? geometry?.kind ?? ''}`.toLowerCase().trim();
  const zoneId = node?.zoneId ?? node?.zone_id ?? geometry?.zoneId ?? null;
  return Boolean(geometry?.isZone || zoneId != null || type === 'zone' || type === 'polygon');
};

const extractNodeLatLng = (node = {}) => {
  const fromCoords = Array.isArray(node?.coords) && node.coords.length >= 2 ? node.coords : null;
  const fromCoordinates =
    Array.isArray(node?.coordinates) && node.coordinates.length >= 2 ? node.coordinates : null;
  const lat = Number(node?.lat ?? node?.latitude ?? fromCoords?.[0] ?? fromCoordinates?.[0]);
  const lng = Number(node?.lng ?? node?.longitude ?? fromCoords?.[1] ?? fromCoordinates?.[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const resolveRadiusMeters = (node = {}, fallbackRadius = DEFAULT_CHECKPOINT_RADIUS_METERS) => {
  const radius = Number(
    node?.radiusMeters ?? node?.radius ?? node?.geometry?.radiusMeters ?? node?.geometry?.radius,
  );
  if (Number.isFinite(radius) && radius > 0) return radius;
  return fallbackRadius;
};

const resolveHandleBearing = (node = {}) => {
  const bearing = Number(
    node?.handleBearing ??
      node?.geometry?.handleBearing ??
      node?.geometry?.angleRad ??
      node?.geometry?.bearingRad,
  );
  return Number.isFinite(bearing) ? bearing : 0;
};

/**
 * Interactive map that visualizes the responder's draft/active tour, route polyline,
 * and draggable checkpoints. Also handles on-map checkpoint creation and ETA updates.
 * Relies on TourContext for shared state. Optional extraLayers allow other overlays
 * (e.g., zones) to render inside the map container.
 * @param {object} props
 * @param {boolean} props.showZoneAnchors
 * @returns {JSX.Element}
 */
export default function TourMap({
  extraLayers = null,
  routeColor = null,
  editable = true,
  viewTour = null,
  focusViewTourOnChange = false,
  addZoneAt = null,
  onZonePlaced = null,
  setZonePlacementPreview = null,
  maxZoom = 23,
  showTourTools = true,
  setShowTourTools = () => {},
  showZoneAnchors = true,
  showPersonnel = true,
  showGoogleLocationSearch = true,
  enableInitialUserLocation = true,
  fallbackMapCenter = [39.8283, -98.5795],
  initialMapZoom = 10,
  showTourToolsPanel = true,
  toolsOverlay = null,
  disableMapReset = false,
  disableLocationSync: disableLocationSyncProp = null,
}) {
  const {
    activeTourData,
    center,
    checkpoints,
    addCheckpoint,
    updateCheckpoint,
    selectedCheckpointId,
    setSelectedCheckpointId,
    mapTool,
    setMapTool,
    stops,
    routeData,
    routeData: contextRouteData,
    startLocation,
    setStartLocation,
    snapToRoads,
    travelMode,
    builderTourData,
  } = useTours();

  const [mapZoom, setMapZoom] = useState(initialMapZoom);
  const [, setCenterSyncTick] = useState(0);
  const mapRef = useRef(null);
  const suppressFocusNext = useRef(false);
  const suppressAutoFitNext = useRef(false);
  const [mapReadyTick, setMapReadyTick] = useState(0);
  const [holdLocationSyncAfterZone, setHoldLocationSyncAfterZone] = useState(false);
  const prevZonePlacementFlowRef = useRef(false);
  const [implicitLocationSyncConsumed, setImplicitLocationSyncConsumed] = useState(false);
  const [showNodeRadii, setShowNodeRadii] = useState(true);
  const lastFocusedViewTourKeyRef = useRef(null);

  const googleApiKey =
    // eslint-disable-next-line no-undef
    (typeof __GMAPS_API_KEY__ !== 'undefined' && __GMAPS_API_KEY__) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.PUBLIC_GOOGLE_MAPS_API_KEY) ||
    (typeof process !== 'undefined' && process.env?.PUBLIC_GOOGLE_MAPS_API_KEY) ||
    (typeof process !== 'undefined' && process.env?.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof window !== 'undefined' && window.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('GOOGLE_MAPS_API_KEY')) ||
    '';

  const { userPosition, hasGeolocated, setUserPosition, setHasGeolocated, effectiveCenter } =
    useUserLocation(center, fallbackMapCenter, enableInitialUserLocation);

  const focusTour = viewTour ?? activeTourData;

  const snapEnabled = snapToRoads && travelMode !== 'INDOOR';
  const isZonePlacementFlow = mapTool?.mode === 'zone';
  const isAwaitingZoneShape =
    mapTool?.mode === 'zone' && !!mapTool?.awaitingShape && !mapTool?.shape;

  const TAIL_DISTANCE_METERS = 10;

  const derivedView = useMemo(() => {
    if (!viewTour) return { checkpoints: [], routeData: null };
    const { originNode, checkpoints: cps } = deriveCheckpointsFromTour(viewTour);
    const polylineStr = viewTour.encodedPolyline || viewTour.polyline || null;
    const route = (() => {
      if (polylineStr || viewTour.legs) {
        return {
          polyline: polylineStr,
          legs: Array.isArray(viewTour.legs) ? viewTour.legs : [],
          totalDuration: viewTour.totalDuration ?? null,
          totalDistanceMeters: viewTour.totalDistanceMeters ?? null,
        };
      }
      return null;
    })();

    const originCp = originNode ? toCheckpoint(originNode, -1) : null;
    const allCps = originCp ? [originCp, ...cps] : cps;

    let enrichedCps = allCps;
    if (polylineStr && allCps.length) {
      const polylinePoints = decode(polylineStr);
      enrichedCps = allCps.map((cp) => {
        const lat = parseFloat(cp.lat);
        const lng = parseFloat(cp.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return cp;
        const pt = [lat, lng];
        const dist = getDistanceToPolyline(pt, polylinePoints);
        if (dist > TAIL_DISTANCE_METERS) {
          const closest = getClosestPointOnPolyline(pt, polylinePoints);
          return { ...cp, additionalEncodedPolyline: encode([pt, closest]) };
        }
        const { additionalPolyline, additionalEncodedPolyline, ...rest } = cp;
        return rest;
      });
    }

    return { checkpoints: enrichedCps, routeData: route };
  }, [viewTour]);

  const rawNodes = focusTour?.nodes ?? activeTourData?.nodes ?? [];
  const renderedNodes =
    Array.isArray(rawNodes) && rawNodes.length
      ? rawNodes.map((node, idx) => toCheckpoint(node, idx))
      : [];

  const renderCheckpoints = renderedNodes.length
    ? renderedNodes
    : Array.isArray(checkpoints) && checkpoints.length
      ? checkpoints
      : derivedView.checkpoints;

  // console.log('[TourMap] render', {
  //   nodes: focusTour?.nodes ?? activeTourData?.nodes,
  //   focusTour,
  //   activeTourData,
  //   checkpoints,
  //   renderCheckpoints,
  //   derivedView,
  // });

  const renderRouteData = contextRouteData || derivedView.routeData || routeData;

  const routeLayerTourData = useMemo(() => {
    const baseTour = activeTourData || focusTour || null;
    if (!baseTour && !renderRouteData) return null;

    return {
      ...(baseTour || {}),
      ...(renderRouteData || {}),
      encodedPolyline:
        renderRouteData?.polyline ?? baseTour?.encodedPolyline ?? baseTour?.polyline ?? null,
      polyline: renderRouteData?.polyline ?? baseTour?.polyline ?? null,
      legs: Array.isArray(renderRouteData?.legs)
        ? renderRouteData.legs
        : Array.isArray(baseTour?.legs)
          ? baseTour.legs
          : [],
    };
  }, [activeTourData, focusTour, renderRouteData]);

  const routeLayerKey = useMemo(() => {
    const encoded = `${routeLayerTourData?.encodedPolyline ?? ''}`;
    const legs = Array.isArray(routeLayerTourData?.legs) ? routeLayerTourData.legs : [];
    const legsSignature = legs
      .map((leg, idx) => `${idx}:${leg?.polyline ?? ''}:${leg?.travelMode ?? leg?.mode ?? ''}`)
      .join('|');
    return `${hashString(encoded)}:${legs.length}:${hashString(legsSignature)}`;
  }, [routeLayerTourData?.encodedPolyline, routeLayerTourData?.legs]);

  const tourStopLatLngs = useMemo(() => {
    const sourceStops = Array.isArray(focusTour?.stops)
      ? focusTour.stops
      : Array.isArray(focusTour?.checkpoints)
        ? focusTour.checkpoints
        : [];
    if (!Array.isArray(sourceStops)) return [];
    return sourceStops
      .map((s) => {
        if (Array.isArray(s?.coordinates)) return toLatLng(s.coordinates);
        return toLatLng(s);
      })
      .filter(Boolean);
  }, [focusTour?.stops, focusTour?.checkpoints]);

  const stopsSignature = useMemo(() => {
    if (!tourStopLatLngs.length) return '';
    return tourStopLatLngs.map((p) => `${round4(p.lat)},${round4(p.lng)}`).join('|');
  }, [tourStopLatLngs]);

  const cumulativeEtaSeconds = useMemo(() => {
    return getCumulativeEtaSeconds(renderRouteData);
  }, [renderRouteData]);

  const legStartsMeters = useMemo(() => {
    return getLegStartsMeters(renderRouteData);
  }, [renderRouteData]);

  const {
    routePoints,
    routeMetrics,
    totalDurationSeconds,
    metersPerDegreeLat,
    metersPerDegreeLng,
  } = useRouteProjection(renderRouteData, focusTour);

  const {
    startIcon,
    handleIcon,
    checkpointIcon,
    computeEtaFor,
    addCheckpointAt,
    addStartAt,
    snapToRoad,
  } = useCheckpointActions({
    routePoints,
    routeMetrics,
    totalDurationSeconds,
    cumulativeEtaSeconds,
    metersPerDegreeLat,
    metersPerDegreeLng,
    checkpoints,
    stops,
    addCheckpoint,
    updateCheckpoint,
    setSelectedCheckpointId,
    snapEnabled,
    googleApiKey,
    startLocation,
    setStartLocation,
    userPosition,
    setUserPosition,
    setHasGeolocated,
    setMapZoom,
    suppressFocusNext,
    suppressAutoFitNext,
    mapRef,
    markerColor: routeColor || '#2e7be7',
  });

  // Editing enabled whenever parent allows (map tool or builder).
  const allowEditing = Boolean(editable);

  useFitBounds({
    mapRef,
    routePoints,
    tripStopLatLngs: tourStopLatLngs,
    stopsSignature,
    mapReadyKey: mapReadyTick,
    suppressNextFitRef: suppressAutoFitNext,
  });

  const findStopById = (id) =>
    stops.find((s) => `${s.id}` === `${id}`) || activeTourData?.stops?.find?.((s) => `${s.id}` === `${id}`);

  const resolveFocusPoints = (node) => {
    if (!node) return [];
    if (isZoneLike(node)) {
      const zonePoints = resolveZonePolygon(node);
      if (Array.isArray(zonePoints) && zonePoints.length >= 3) {
        return zonePoints;
      }
    }
    const point = toLatLng(node) || toLatLng(node?.coordinates);
    return point ? [point] : [];
  };

  const buildFocusPointsKey = (points = []) => {
    if (!Array.isArray(points) || !points.length) return '';
    return points
      .map((point) => {
        const lat = Number(point?.lat ?? point?.[0]);
        const lng = Number(point?.lng ?? point?.[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
        return `${round4(lat)},${round4(lng)}`;
      })
      .filter(Boolean)
      .join('|');
  };

  const selectedCheckpointKey = useMemo(() => {
    if (selectedCheckpointId === 'start') {
      const focusPoints = resolveFocusPoints(startLocation);
      return focusPoints.length ? `start:${buildFocusPointsKey(focusPoints)}` : '';
    }
    if (typeof selectedCheckpointId === 'string' && selectedCheckpointId.startsWith('stop-')) {
      const stopId = selectedCheckpointId.replace('stop-', '');
      const focusPoints = resolveFocusPoints(findStopById(stopId));
      return focusPoints.length ? `stop:${stopId}:${buildFocusPointsKey(focusPoints)}` : '';
    }
    const cp = checkpoints.find((c) => c.id === selectedCheckpointId);
    const focusPoints = resolveFocusPoints(cp);
    return cp && focusPoints.length ? `${cp.id}:${buildFocusPointsKey(focusPoints)}` : '';
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkpoints,
    selectedCheckpointId,
    stops,
    activeTourData,
    startLocation,
  ]);

  // Focus a checkpoint or stop on the map when requested
  useEffect(() => {
    if (!mapRef.current || !selectedCheckpointId) return;
    if (suppressFocusNext.current) {
      suppressFocusNext.current = false;
      return;
    }
    let targetPoints = [];
    if (selectedCheckpointId === 'start') {
      targetPoints = resolveFocusPoints(startLocation);
    } else if (
      typeof selectedCheckpointId === 'string' &&
      selectedCheckpointId.startsWith('stop-')
    ) {
      const stopId = selectedCheckpointId.replace('stop-', '');
      targetPoints = resolveFocusPoints(findStopById(stopId));
    } else {
      targetPoints = resolveFocusPoints(
        checkpoints.find((c) => c.id === selectedCheckpointId),
      );
    }
    if (!targetPoints.length) return;
    try {
      focusMapOnGeometry(mapRef.current, targetPoints, {
        padding: [80, 80],
        maxZoom: 17,
        singlePointMinZoom: 15,
        singlePointMaxZoom: 17,
        animate: true,
      });
    } catch (err) {
      console.warn('focus on checkpoint failed', err);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCheckpointId, selectedCheckpointKey, startLocation]);

  const mapCenterProp = effectiveCenter;
  const stableCenterRef = useRef(mapCenterProp);
  const lastCenterZoomSyncKeyRef = useRef(null);

  const centerLatLng = useMemo(() => parseCenterLatLng(center), [center]);
  const routeStart = tourStopLatLngs.length ? tourStopLatLngs[0] : null;
  const centerMatchesRouteStart =
    !centerLatLng ||
    (routeStart &&
      almostEqual(centerLatLng.lat, routeStart.lat) &&
      almostEqual(centerLatLng.lng, routeStart.lng));
  const hasFitGeometry = routePoints.length > 1 || tourStopLatLngs.length > 1;
  const hasExplicitCenter = Boolean(centerLatLng);
  const disableImplicitLocationSync = !hasExplicitCenter && implicitLocationSyncConsumed;
  const disableLocationSync =
    typeof disableLocationSyncProp === 'boolean'
      ? disableLocationSyncProp
      : hasFitGeometry && centerMatchesRouteStart;
  const effectiveDisableLocationSync =
    disableLocationSync ||
    isZonePlacementFlow ||
    holdLocationSyncAfterZone ||
    disableImplicitLocationSync;

  useEffect(() => {
    // When user-location centering is disabled and there is no explicit center,
    // consume implicit sync so fallback center is not re-applied after edits/clear.
    if (!hasExplicitCenter && (!enableInitialUserLocation || hasGeolocated)) {
      setImplicitLocationSyncConsumed(true);
    }
  }, [enableInitialUserLocation, hasExplicitCenter, hasGeolocated]);

  useLayoutEffect(() => {
    const wasZonePlacementFlow = prevZonePlacementFlowRef.current;
    prevZonePlacementFlowRef.current = isZonePlacementFlow;
    if (!wasZonePlacementFlow || isZonePlacementFlow) return;

    const liveZoom = Number(mapRef.current?.getZoom?.());
    if (Number.isFinite(liveZoom)) {
      setMapZoom((prev) => (prev === liveZoom ? prev : liveZoom));
    }
    setHoldLocationSyncAfterZone(true);
  }, [isZonePlacementFlow]);

  useEffect(() => {
    if (!holdLocationSyncAfterZone) return undefined;
    const release = () => {
      setHoldLocationSyncAfterZone(false);
    };
    if (typeof window?.requestAnimationFrame === 'function') {
      const rafId = window.requestAnimationFrame(release);
      return () => window.cancelAnimationFrame(rafId);
    }
    const timeoutId = setTimeout(release, 0);
    return () => clearTimeout(timeoutId);
  }, [holdLocationSyncAfterZone]);

  // Allow callers to pass an optional zoom as [lat, lng, zoom]
  useEffect(() => {
    if (!center) return;
    const parts = Array.isArray(center)
      ? center
      : [center.lat ?? center.latitude, center.lng ?? center.longitude, center.zoom];
    const zoomMaybe = Number(parts?.[2]);
    if (Number.isFinite(zoomMaybe)) {
      const clamped = Math.min(Math.max(zoomMaybe, 10), 18);
      const key = `${Number(parts?.[0])}:${Number(parts?.[1])}:${clamped}`;
      if (key === lastCenterZoomSyncKeyRef.current) return;
      lastCenterZoomSyncKeyRef.current = key;
      if (effectiveDisableLocationSync) return;
      setMapZoom(clamped);
    }
  }, [center, effectiveDisableLocationSync]);

  useEffect(() => {
    if (!effectiveDisableLocationSync) {
      stableCenterRef.current = mapCenterProp;
      return;
    }
    if (!stableCenterRef.current) {
      stableCenterRef.current = mapCenterProp;
    }
  }, [effectiveDisableLocationSync, mapCenterProp]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.on) return undefined;
    const syncZoom = () => {
      const nextZoom = Number(map.getZoom?.());
      if (!Number.isFinite(nextZoom)) return;
      setMapZoom((prev) => (almostEqual(prev, nextZoom) ? prev : nextZoom));
    };
    syncZoom();
    map.on('zoomend', syncZoom);
    return () => {
      map.off('zoomend', syncZoom);
    };
  }, [mapReadyTick]);

  useEffect(() => {
    if (!effectiveDisableLocationSync) return undefined;
    const map = mapRef.current;
    if (!map?.on) return undefined;
    const updateCenter = () => {
      const next = map.getCenter?.();
      if (!next) return;
      const nextCenter = [Number(next.lat), Number(next.lng)];
      const prev = stableCenterRef.current;
      if (!prev || Number(prev[0]) !== nextCenter[0] || Number(prev[1]) !== nextCenter[1]) {
        stableCenterRef.current = nextCenter;
        setCenterSyncTick((value) => value + 1);
      }
    };
    map.on('moveend', updateCenter);
    return () => {
      map.off('moveend', updateCenter);
    };
  }, [effectiveDisableLocationSync, mapReadyTick]);

  const viewStartLatLng = useMemo(() => resolveTourStartLatLng(viewTour), [viewTour]);

  const viewTourFocusKey = useMemo(
    () => buildTourGeometryFocusKey({ viewTour, routePoints, stopsSignature }),
    [viewTour, routePoints, stopsSignature],
  );

  useEffect(() => {
    if (!focusViewTourOnChange || !viewTourFocusKey) return;
    if (lastFocusedViewTourKeyRef.current === viewTourFocusKey) return;
    const map = mapRef.current;
    if (!map) return;
    lastFocusedViewTourKeyRef.current = viewTourFocusKey;
    try {
      if (routePoints.length > 1) {
        focusMapOnGeometry(map, routePoints, {
          padding: [80, 80],
          maxZoom: 17,
        });
        return;
      }
      if (tourStopLatLngs.length > 1) {
        focusMapOnGeometry(map, tourStopLatLngs, {
          padding: [80, 80],
          maxZoom: 17,
        });
        return;
      }
      const focusPoint = tourStopLatLngs[0] || viewStartLatLng || null;
      if (focusPoint) {
        focusMapOnGeometry(map, [focusPoint], {
          padding: [80, 80],
          maxZoom: 17,
          singlePointMinZoom: 15,
          singlePointMaxZoom: 17,
          animate: true,
        });
      }
    } catch (error) {
      console.warn('[TourMap] Failed to focus view tour', error);
    }
  }, [focusViewTourOnChange, routePoints, tourStopLatLngs, viewStartLatLng, viewTourFocusKey]);

  const resolvedMapCenter = effectiveDisableLocationSync ? stableCenterRef.current : mapCenterProp;

  const startLatLng = useMemo(() => {
    const rawLat = startLocation?.lat;
    const rawLng = startLocation?.lng;
    const hasExplicit =
      rawLat !== '' &&
      rawLng !== '' &&
      rawLat !== null &&
      rawLng !== null &&
      rawLat !== undefined &&
      rawLng !== undefined;
    if (hasExplicit) {
      const lat = Number(rawLat);
      const lng = Number(rawLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }
    return viewStartLatLng;
  }, [startLocation?.lat, startLocation?.lng, viewStartLatLng]);

  const startIsZone = useMemo(() => isZoneLike(startLocation), [startLocation]);

  const startRadiusMeters = useMemo(
    () => resolveRadiusMeters(startLocation, DEFAULT_ORIGIN_RADIUS_METERS),
    [startLocation],
  );

  const startHandleBearing = useMemo(() => resolveHandleBearing(startLocation), [startLocation]);

  const loopDestinationRadiusMeters = useMemo(() => {
    const radius = Number(
      startLocation?.loopDestinationRadiusMeters ?? startLocation?.returnRadiusMeters,
    );
    if (Number.isFinite(radius) && radius > 0) return radius;
    return DEFAULT_DESTINATION_RADIUS_METERS;
  }, [startLocation?.loopDestinationRadiusMeters, startLocation?.returnRadiusMeters]);

  const loopDestinationHandleBearing = useMemo(() => {
    const bearing = Number(
      startLocation?.loopDestinationHandleBearing ?? startLocation?.returnHandleBearing,
    );
    return Number.isFinite(bearing) ? bearing : 0;
  }, [startLocation?.loopDestinationHandleBearing, startLocation?.returnHandleBearing]);

  const isLoopedTour = useMemo(() => {
    if (typeof focusTour?.isLooped === 'boolean') return focusTour.isLooped;
    if (typeof activeTourData?.isLooped === 'boolean') return activeTourData.isLooped;
    if (typeof builderTourData?.isLooped === 'boolean') return builderTourData.isLooped;
    return false;
  }, [activeTourData?.isLooped, builderTourData?.isLooped, focusTour?.isLooped]);

  const findCheckpointByNode = (latLng, id) => {
    if (!Array.isArray(checkpoints) || !checkpoints.length) return null;
    if (id != null) {
      const byId = checkpoints.find((cp) => `${cp?.id}` === `${id}`);
      if (byId) return byId;
    }
    if (!latLng) return null;
    return checkpoints.find((cp) => {
      const cpLat = Number(cp?.lat ?? cp?.coordinates?.[0] ?? cp?.coords?.[0]);
      const cpLng = Number(cp?.lng ?? cp?.coordinates?.[1] ?? cp?.coords?.[1]);
      if (!Number.isFinite(cpLat) || !Number.isFinite(cpLng)) return false;
      return sameLatLng({ lat: cpLat, lng: cpLng }, latLng);
    });
  };

  const boundaryNodeRadii = useMemo(() => {
    const fromTourNodes = Array.isArray(focusTour?.nodes) ? focusTour.nodes : [];
    if (fromTourNodes.length) {
      return fromTourNodes
        .map((node, idx) => {
          const nodeLatLng = extractNodeLatLng(node);
          if (!nodeLatLng || isZoneLike(node)) return null;

          const nodeKind = normalizeNodeKind(
            node?.type ?? node?.kind ?? (idx === 0 ? 'origin' : 'checkpoint'),
          );
          const matchedCheckpoint = findCheckpointByNode(nodeLatLng, node?.id);

          if (nodeKind !== 'origin' && nodeKind !== 'destination') return null;

          if (nodeKind === 'destination' && matchedCheckpoint) return null;

          if (nodeKind === 'origin') {
            const drawAt = startLatLng || nodeLatLng;
            return {
              id: node?.id ?? 'origin',
              key: `origin-${node?.id ?? idx}`,
              kind: 'origin',
              lat: drawAt.lat,
              lng: drawAt.lng,
              radiusMeters: startRadiusMeters,
              handleBearing: startHandleBearing,
              canEdit: allowEditing && !startIsZone,
              targetType: 'start',
            };
          }

          const drawAt = startLatLng || nodeLatLng;
          const useReturnControls = startLatLng && sameLatLng(nodeLatLng, startLatLng);
          return {
            id: node?.id ?? `destination-${idx}`,
            key: `destination-${node?.id ?? idx}`,
            kind: 'destination',
            lat: drawAt.lat,
            lng: drawAt.lng,
            radiusMeters: useReturnControls
              ? loopDestinationRadiusMeters
              : resolveRadiusMeters(node, DEFAULT_DESTINATION_RADIUS_METERS),
            handleBearing: useReturnControls
              ? loopDestinationHandleBearing
              : resolveHandleBearing(node),
            canEdit: allowEditing && useReturnControls && !startIsZone,
            targetType: useReturnControls ? 'loopDestination' : null,
          };
        })
        .filter(Boolean);
    }

    const fallback = [];
    if (startLatLng && !startIsZone) {
      fallback.push({
        id: startLocation?.id ?? 'origin',
        key: 'origin-fallback',
        kind: 'origin',
        lat: startLatLng.lat,
        lng: startLatLng.lng,
        radiusMeters: startRadiusMeters,
        handleBearing: startHandleBearing,
        canEdit: allowEditing,
        targetType: 'start',
      });
    }
    if (isLoopedTour && startLatLng && !startIsZone) {
      fallback.push({
        id: 'loop-destination',
        key: 'destination-loop-fallback',
        kind: 'destination',
        lat: startLatLng.lat,
        lng: startLatLng.lng,
        radiusMeters: loopDestinationRadiusMeters,
        handleBearing: loopDestinationHandleBearing,
        canEdit: allowEditing,
        targetType: 'loopDestination',
      });
    }
    return fallback;
  }, [
    allowEditing,
    checkpoints,
    findCheckpointByNode,
    focusTour?.nodes,
    isLoopedTour,
    loopDestinationHandleBearing,
    loopDestinationRadiusMeters,
    startHandleBearing,
    startIsZone,
    startLatLng,
    startLocation?.id,
    startRadiusMeters,
  ]);

  const handleBoundaryRadiusChange = (entry, nextRadius, nextBearing) => {
    const radiusMeters = Math.max(5, Math.round(Number(nextRadius) || 0));
    const handleBearing = Number.isFinite(nextBearing) ? nextBearing : 0;
    if (!entry || !Number.isFinite(radiusMeters) || radiusMeters <= 0) return;

    if (entry.targetType === 'start') {
      setStartLocation((prev) => {
        const prevGeometry = prev?.geometry || {};
        const preserveZone = isZoneLike(prev);
        return {
          ...prev,
          radiusMeters,
          handleBearing,
          geometry: preserveZone
            ? prevGeometry
            : {
                ...prevGeometry,
                type: prevGeometry?.type || 'circle',
                radiusMeters,
                angleRad: handleBearing,
                handleBearing,
              },
        };
      });
      return;
    }

    if (entry.targetType === 'loopDestination') {
      setStartLocation((prev) => ({
        ...prev,
        loopDestinationRadiusMeters: radiusMeters,
        loopDestinationHandleBearing: handleBearing,
      }));
      return;
    }

    if (entry.targetType === 'checkpoint' && entry.targetId != null) {
      const existing = checkpoints.find((cp) => `${cp?.id}` === `${entry.targetId}`) || {};
      const existingGeometry = existing?.geometry || {};
      updateCheckpoint(entry.targetId, {
        radiusMeters,
        handleBearing,
        geometry: {
          ...existingGeometry,
          type: existingGeometry?.type || 'circle',
          radiusMeters,
          angleRad: handleBearing,
          handleBearing,
        },
      });
    }
  };

  const handleBoundaryNodeSelect = (entry) => {
    if (!entry) return;
    suppressFocusNext.current = true;
    if (entry.targetType === 'start' || entry.targetType === 'loopDestination') {
      setSelectedCheckpointId('start');
    }
  };

  const startZonePoints = useMemo(() => {
    const geometry = startLocation?.geometry || {};
    const zoneId = startLocation?.zoneId ?? geometry?.zoneId ?? null;
    const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
    const isZone = geometry?.isZone || type === 'zone' || type === 'polygon' || zoneId != null;
    if (!isZone) return [];
    const points = resolveZonePolygon(startLocation);
    return Array.isArray(points) && points.length >= 3 ? points : [];
  }, [startLocation]);

  const routeConnectors = useMemo(() => {
    const connectors = [];
    const legs = Array.isArray(routeLayerTourData?.legs)
      ? routeLayerTourData.legs.filter((l) => l?.polyline)
      : [];
    const polylineStr = routeLayerTourData?.encodedPolyline;

    let polylinePoints = null;

    if (legs.length) {
      polylinePoints = legs.flatMap((leg) => decode(leg.polyline));
    } else if (polylineStr) {
      polylinePoints = decode(polylineStr);
    }

    if (!polylinePoints || polylinePoints.length < 2) return connectors;

    if (startLatLng) {
      const pt = [startLatLng.lat, startLatLng.lng];
      const dist = getDistanceToPolyline(pt, polylinePoints);
      if (dist > TAIL_DISTANCE_METERS) {
        connectors.push({
          key: 'origin',
          positions: [pt, getClosestPointOnPolyline(pt, polylinePoints)],
        });
      }
    }

    const nodes = Array.isArray(focusTour?.nodes) ? focusTour.nodes : [];
    const stops = Array.isArray(focusTour?.stops) ? focusTour.stops : [];
    const destNode =
      nodes.find((n) => `${n?.type || n?.role || ''}`.toLowerCase() === 'destination') ||
      stops.find((s) => `${s?.kind || s?.type || ''}`.toLowerCase() === 'destination') ||
      null;
    const destLatLng = destNode ? toLatLng(destNode) || toLatLng(destNode.coordinates) : null;

    if (destLatLng) {
      const pt = [destLatLng.lat, destLatLng.lng];
      const dist = getDistanceToPolyline(pt, polylinePoints);
      if (dist > TAIL_DISTANCE_METERS) {
        connectors.push({
          key: 'destination',
          positions: [pt, getClosestPointOnPolyline(pt, polylinePoints)],
        });
      }
    }

    return connectors;
  }, [routeLayerTourData, startLatLng, focusTour]);

  const canShowGoogleLocationSearch = showGoogleLocationSearch && Boolean(googleApiKey);

  return (
    <div
      className={`position-relative w-100 h-100 ${isAwaitingZoneShape ? 'zone-shape-guided' : ''}`}
      style={{ isolation: 'isolate' }}
    >
      <BaseMap
        mapCenter={resolvedMapCenter}
        zoom={mapZoom}
        maxZoom={maxZoom}
        mapRef={mapRef}
        scrollWheelZoom={true}
        disableLocationSync={effectiveDisableLocationSync}
        disableMapReset={disableMapReset}
        preserveLocationZoom={true}
        onMapReady={() => setMapReadyTick((prev) => prev + 1)}
      >
        {canShowGoogleLocationSearch && <GoogleLocationSearchControl googleApiKey={googleApiKey} />}
        {allowEditing && (
          <MapClickHandler
            mapTool={mapTool}
            addCheckpointAt={(latlng, geometryType) =>
              addCheckpointAt(latlng, geometryType, legStartsMeters)
            }
            addStartAt={addStartAt}
            addZoneAt={addZoneAt}
            onZonePlaced={onZonePlaced}
            setMapTool={setMapTool}
            setZonePlacementPreview={setZonePlacementPreview}
            legStartsMeters={legStartsMeters}
          />
        )}
        <RouteLayer key={routeLayerKey} activeTourData={routeLayerTourData} />
        {routeConnectors.map((connector) => (
          <Polyline
            key={connector.key}
            positions={connector.positions}
            pathOptions={{ color: 'blue', weight: 3, dashArray: '6 12' }}
          />
        ))}
        <CheckpointsLayer
          checkpoints={renderCheckpoints}
          selectedCheckpointId={selectedCheckpointId}
          setSelectedCheckpointId={setSelectedCheckpointId}
          handleIcon={handleIcon}
          checkpointIcon={checkpointIcon}
          metersPerDegreeLat={metersPerDegreeLat}
          metersPerDegreeLng={metersPerDegreeLng}
          snapToRoad={snapToRoad}
          computeEtaFor={(latlng) => computeEtaFor(latlng, legStartsMeters)}
          updateCheckpoint={updateCheckpoint}
          suppressFocusNext={suppressFocusNext}
          legStartsMeters={legStartsMeters}
          editable={allowEditing}
          showZoneAnchors={showZoneAnchors && showNodeRadii}
          showNodeRadii={showNodeRadii}
        />
        <NodeRadiusLayer
          nodes={boundaryNodeRadii}
          visible={showNodeRadii}
          editable={allowEditing}
          handleIcon={handleIcon}
          metersPerDegreeLat={metersPerDegreeLat}
          metersPerDegreeLng={metersPerDegreeLng}
          onChangeRadius={handleBoundaryRadiusChange}
          onSelectNode={handleBoundaryNodeSelect}
        />
        {showPersonnel && <PersonnelLayerWithLiveData />}
        {extraLayers}
        {showNodeRadii && startZonePoints.length >= 3 && (
          <Polygon
            positions={startZonePoints}
            pathOptions={{
              color: '#d9534f',
              fillColor: '#d9534f',
              fillOpacity: 0.15,
              weight: 2,
            }}
            interactive={false}
          />
        )}
        {startLatLng && (
          <Marker position={startLatLng} icon={startIcon} interactive={allowEditing} />
        )}
      </BaseMap>
      <div className="tour-map-radius-toggle">
        <Form.Check
          type="switch"
          id="toggle-node-radii"
          label="Show/hide geofenced areas"
          checked={showNodeRadii}
          onChange={(e) => setShowNodeRadii(e.target.checked)}
        />
      </div>
      {isAwaitingZoneShape && <div className="zone-shape-guided-mask" />}
      {isZonePlacementFlow && (
        <div className="zone-shape-guided-instruction">
          {isAwaitingZoneShape ? (
            <>
              Pick a zone shape in Zone Tools. Press <kbd>Esc</kbd> to cancel.
            </>
          ) : (
            <>
              Click on the map to place the zone. Press <kbd>Esc</kbd> to cancel.
            </>
          )}
        </div>
      )}
      {toolsOverlay}

      {showTourTools && showTourToolsPanel && (
        <TourToolsPanel
          mapTool={mapTool}
          setMapTool={setMapTool}
          showTourTools={showTourTools}
          setShowTourTools={setShowTourTools}
        />
      )}

      {/* For possible future use: repositioned checkpoint editing onto cards, keeping overlay commented out. */}
      {/* <CheckpointOverlay
        checkpoint={
          checkpoints.find((cp) => cp.id === selectedCheckpointId) || null
        }
        etaSeconds={
          checkpoints.find((cp) => cp.id === selectedCheckpointId)
            ?.etaSeconds ?? null
        }
        onUpdate={(updates) => {
          if (!selectedCheckpointId) return;
          updateCheckpoint(selectedCheckpointId, updates);
        }}
        onClose={() => setSelectedCheckpointId(null)}
        onDelete={() => {
          if (!selectedCheckpointId) return;
          removeCheckpoint(selectedCheckpointId);
        }}
      /> */}
    </div>
  );
}
